import React, { useState, useEffect, useMemo } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    Input as AntInput,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';

import { fetcProductData } from '../../../../redux/slice/masterSlice';
import type {
    BrandNames,
    GST,
    Size,
    Hc
} from '../../../../types/CreateProductType';
import BrandNameModel from '../../masters/brand-names/BrandNameModel';
import CategoryModal from '../../Category/CategoryModal';

import UnitTypeModel from '../../masters/unit-types/UnitTypeModel';
import SizeModel from '../../masters/sizes/SizeModel';
import GSTModal from '../../masters/gst/GSTModal';
import HcModal from '../../masters/hsn-codes/HcModal';


interface ProductModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        product_id?: number;
        product_name?: string;
        product_code?: string;       // added
        product_price?: number;      // added
        product_unit_value?: number;      // added
        product_unit?: number;      // added
        product_qty?: number;      // added
        product_mrp?: number;      // added
        product_discount_price?: number;      // added
        product_category?: number;      // added
        category_id?: number;      // added
        brand_id?: number;      // added
        product_brand_name?: number;      // added
        product_size?: number;      // added
        product_gst?: number;      // added
        product_hsn_code?: number; // added

    } | null;
    isEditMode?: boolean;
}
interface Categories {
    category_id: number;
    category_name: string;
    category_parent?: number;
    parent_name?: string;
    category_status?: number;
    category_short_code?: string;
}
type FetchKey =
    | 'categories'
    | 'brands'
    | 'hsnCodes';

const ProductModal: React.FC<ProductModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {

    const { categories, unittypes, sizes, gsts, loaded } = useAppSelector(state => state.master);
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [brandnames, setBrandNames] = useState<BrandNames[]>([]);
    const [selectedBrandNames, setSelectedBrandNames] = useState<number | null>(null);
    const [isBrandNameModalVisible, setIsBrandNameModalVisible] = useState(false);

    const [selectedCategories, setSelectedCategories] = useState<number | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined | null>(null);
    const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
    const [isUnitTypeModalVisible, setIsUnitTypeModalVisible] = useState(false);
    const [isSizeModalVisible, setIsSizeModalVisible] = useState(false);
    const [isGstModalVisible, setIsGstModalVisible] = useState(false);
    const [isHsnModalVisible, setIsHsnModalVisible] = useState(false);
    const [hsnCodes, setHsnCodes] = useState<Hc[]>([]);

    const groupedCategoryOptions = useMemo(() => {
        if (!categories || categories.length === 0) return [];

        const catMap = new Map<number, (typeof categories)[0]>();
        categories.forEach(c => catMap.set(c.category_id, c));

        // Get full path (e.g. "Root > Main -> Sub Category")
        const getCategoryPath = (cat: (typeof categories)[0]): string => {
            const path: string[] = [cat.category_name];
            let current = cat;
            let depth = 0;
            while (current.category_parent && current.category_parent !== 0 && depth < 10) {
                const parent = catMap.get(current.category_parent);
                if (!parent || parent.category_id === current.category_id) break;
                path.unshift(parent.category_name);
                current = parent;
                depth++;
            }
            return path.join(' -> ');
        };

        const getRootCategory = (cat: (typeof categories)[0]): (typeof categories)[0] => {
            let current = cat;
            let depth = 0;
            while (current.category_parent && current.category_parent !== 0 && depth < 10) {
                const parent = catMap.get(current.category_parent);
                if (!parent || parent.category_id === current.category_id) break;
                current = parent;
                depth++;
            }
            return current;
        };

        // Group by Root / Top-Level Category
        const groupMap = new Map<number, { root: (typeof categories)[0]; items: typeof categories }>();

        categories.forEach(cat => {
            const root = getRootCategory(cat);
            if (!groupMap.has(root.category_id)) {
                groupMap.set(root.category_id, { root, items: [] });
            }
            groupMap.get(root.category_id)!.items.push(cat);
        });

        // Construct Select options with optgroup
        const optGroups: { label: string; options: { label: string; value: number }[] }[] = [];

        groupMap.forEach(({ root, items }) => {
            // Sort items so root item is at top, followed by sub categories
            const sortedItems = [...items].sort((a, b) => {
                if (a.category_id === root.category_id) return -1;
                if (b.category_id === root.category_id) return 1;
                return a.category_name.localeCompare(b.category_name);
            });

            const formattedOptions = sortedItems.map(item => {
                const isRoot = !item.category_parent || item.category_parent === 0;
                const path = getCategoryPath(item);
                return {
                    label: isRoot ? `${item.category_name} (Main Category)` : path,
                    value: item.category_id,
                };
            });

            optGroups.push({
                label: `📂 ${root.category_name}`,
                options: formattedOptions,
            });
        });

        return optGroups;
    }, [categories]);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    // Handle form submit
    const handleAddProduct = async (values: any) => {
        try {
            const dataVal = {
                product_name: values.product_name,
                product_code: values.product_code,
                product_category: values.product_category,
                product_brand_name: values.product_brand_name,
                product_price: parseFloat(values.product_price),  // convert to number
                product_mrp: parseFloat(values.product_mrp),  // convert to number
                product_discount_price: parseFloat(values.product_discount_price),  // convert to number
                product_unit: parseFloat(values.product_unit),  // convert to number
                product_qty: parseFloat(values.product_qty),  // convert to number
                product_unit_value: parseFloat(values.product_unit_value),  // convert to number
                product_size: values.product_size,  // convert to number
                product_gst: values.product_gst,  // convert to number
                product_hsn_code: values.product_hsn_code,
                hc_id: values.product_hsn_code,
                hsn_id: values.product_hsn_code,
                ...(initialValues?.product_id && { product_id: initialValues.product_id }),
            };
            const url = isEditMode ? 'edit-medical-product' : 'add-medical-product';
            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });

            onCreate(data);
            showSuccess(isEditMode ? 'Product updated successfully!' : 'Product created successfully!');
            // form.resetFields();

            if (isModalClose) {
                onCancel();
            }

            dispatch(fetcProductData());
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} product`);
        }
    };

    // Brand name
    const handleBrandNameCreated = (newBrandName: BrandNames) => {
        fetchAllDataSequentially(['brands']);
        setSelectedBrandNames(newBrandName.brand_id);
        setIsBrandNameModalVisible(false);
    };
    const fetchAllDataSequentially = async (
        keys: FetchKey[] = ['categories', 'brands', 'hsnCodes']
    ) => {
        try {
            if (keys.includes('brands')) {
                const brandRes: any = await apiRequest({ method: 'get', url: 'brand?status=1' });
                setBrandNames(Array.isArray(brandRes) ? brandRes : (brandRes?.data || []));
            }
            if (keys.includes('hsnCodes')) {
                const hsnRes: any = await apiRequest({ method: 'get', url: 'hsn-codes' });
                setHsnCodes(Array.isArray(hsnRes) ? hsnRes : (hsnRes?.data || []));
            }
        } catch (error) {
            showError('Failed to fetch some data');
            console.error(error);
        }
    };
    const debouncedFetchAllData = useMemo(
        () =>
            debounce((keys?: FetchKey[]) => {
                fetchAllDataSequentially(keys);
            }, 300),
        []
    );

    // Category
    const handleCategoryCreated = (newCategory: Categories) => {
        fetchAllDataSequentially(['categories']);
        setSelectedCategories(newCategory.category_id);
        setIsCategoryModalVisible(false);
    };
    const handleUnitTypeCreated = (newCategory: Categories) => {
        fetchAllDataSequentially(['categories']);
        setSelectedCategories(newCategory.category_id);
        setIsCategoryModalVisible(false);
    };
    const handleSizeCreated = (newCategory: Size) => {
        setIsSizeModalVisible(false);
    };
    const handleGstCreated = (newCategory: GST) => {
        setIsGstModalVisible(false);
    };
    const handleHsnCreated = (newHsn: Hc) => {
        fetchAllDataSequentially(['hsnCodes']);
        setIsHsnModalVisible(false);
        form.setFieldsValue({ product_hsn_code: newHsn.hc_id });
    };
    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible) {
            fetchAllDataSequentially();
        }
        if (visible && isEditMode && initialValues) {
            const hsnVal = initialValues.product_hsn_code ?? (initialValues as any).hc_id ?? (initialValues as any).hsn_id ?? (initialValues as any).product_hsn ?? (initialValues as any).hsn_code;
            form.setFieldsValue({
                product_name: initialValues.product_name,
                product_code: initialValues.product_code,
                product_brand_name: initialValues.brand_id || initialValues.product_brand_name,
                product_category: initialValues.category_id || initialValues.product_category,
                product_price: initialValues.product_price?.toString(),
                product_mrp: initialValues.product_mrp?.toString(),
                product_discount_price: initialValues.product_discount_price?.toString(),
                product_unit: initialValues.product_unit,
                product_qty: initialValues.product_qty?.toString(),
                product_unit_value: initialValues.product_unit_value?.toString(),
                product_size: initialValues.product_size,
                product_gst: initialValues.product_gst,
                product_hsn_code: hsnVal ? (Number(hsnVal) || hsnVal) : undefined,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);

    return (
        <Modal
            title={isEditMode ? 'Edit Product' : 'Add New Product'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddProduct}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_name"
                            className="px-8"
                            label={
                                <span>
                                    Product Name&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Product Name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input showCount maxLength={255} placeholder="e.g., Product" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_code"
                            label="Product Code"
                            rules={[
                                { required: true, message: 'Please enter the Product Code' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Input maxLength={50} placeholder="e.g., PROD1234" />
                        </Form.Item>
                    </Col>
                    {/* Brand */}
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Brand Name
                        </div>
                        <Form.Item>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Form.Item noStyle name="product_brand_name">
                                    <Select
                                        showSearch
                                        placeholder="Select Brand Name"
                                        style={{ flex: 1, minWidth: 0 }}
                                        onChange={(value) => {

                                        }}
                                        options={[
                                            { label: 'Select Brand Name', value: null },
                                            ...brandnames.map((brand) => ({
                                                label: brand.brand_name,
                                                value: brand.brand_id,
                                            })),
                                        ]}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                        }
                                        dropdownMatchSelectWidth={false}
                                    />

                                </Form.Item>
                                <Button
                                    style={{ padding: 0, flexShrink: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsBrandNameModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>
                        <BrandNameModel
                            visible={isBrandNameModalVisible}
                            onCancel={() => setIsBrandNameModalVisible(false)}
                            onCreate={handleBrandNameCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    {/* category */}
                    <Col xs={24} sm={12} md={8} >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Category
                        </div>
                        <Form.Item>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Form.Item
                                    noStyle
                                    name="product_category"
                                    rules={[{ required: true, message: 'Please select a category!' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select Category"
                                        onChange={(value) => {
                                            // Update another field based on selected value
                                            const selected = categories.find((cat) => cat.category_id === value);
                                            form.setFieldsValue({
                                                product_code: selected?.category_short_code || '',
                                            });
                                        }}
                                        options={groupedCategoryOptions}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                        }
                                        dropdownMatchSelectWidth={false}
                                        style={{ flex: 1, minWidth: 0 }}
                                    />
                                </Form.Item>

                                <Button
                                    style={{ padding: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsCategoryModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>
                        {/* Reusable GST Modal */}
                        <CategoryModal
                            visible={isCategoryModalVisible}
                            onCancel={() => setIsCategoryModalVisible(false)}
                            onCreate={handleCategoryCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Size
                        </div>
                        <Form.Item>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Form.Item
                                    noStyle
                                    name="product_size"
                                    rules={[{ required: true, message: 'Please select a product size!' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select product size"
                                        onChange={(value) => {

                                            // Optional: do something with the selected unit
                                            const selected = sizes.find((size) => size.size_id === value);
                                            console.log('Selected product size:', selected);
                                        }}
                                        options={[
                                            ...(sizes || []).map((size) => ({
                                                label: size.size_name,
                                                value: size.size_id,
                                            })),
                                        ]}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                        }
                                        style={{ flex: 1, minWidth: 0 }}
                                    />
                                </Form.Item>

                                <Button
                                    style={{ padding: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsSizeModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>

                        {/* Reusable GST Modal */}
                        <SizeModel
                            visible={isSizeModalVisible}
                            onCancel={() => setIsSizeModalVisible(false)}
                            onCreate={handleSizeCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            GST
                        </div>
                        <Form.Item>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Form.Item
                                    noStyle
                                    name="product_gst"
                                    rules={[{ required: true, message: 'Please select a product gst!' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select product size"
                                        onChange={(value) => {

                                            // Optional: do something with the selected unit
                                            const selected = gsts.find((gst) => gst.gst_id === value);
                                            console.log('Selected product gst:', selected);
                                        }}
                                        options={[
                                            ...(gsts || []).map((size) => ({
                                                label: size.gst_name,
                                                value: size.gst_id,
                                            })),
                                        ]}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                        }
                                        style={{ flex: 1, minWidth: 0 }}
                                    />
                                </Form.Item>

                                <Button
                                    style={{ padding: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsGstModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>

                        {/* Reusable GST Modal */}
                        <GSTModal
                            visible={isGstModalVisible}
                            onCancel={() => setIsGstModalVisible(false)}
                            onCreate={handleGstCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            HSN Code
                        </div>
                        <Form.Item>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Form.Item
                                    noStyle
                                    name="product_hsn_code"
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select HSN Code"
                                        options={[
                                            ...(hsnCodes || []).map((hsn) => ({
                                                label: hsn.hc_name,
                                                value: hsn.hc_id,
                                            })),
                                        ]}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                        }
                                        style={{ flex: 1, minWidth: 0 }}
                                    />
                                </Form.Item>

                                <Button
                                    style={{ padding: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsHsnModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>

                        <HcModal
                            visible={isHsnModalVisible}
                            onCancel={() => setIsHsnModalVisible(false)}
                            onCreate={handleHsnCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Unit Type
                        </div>
                        <Form.Item>
                            <AntInput.Group compact style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
                                <Form.Item
                                    noStyle
                                    name="product_unit"
                                    rules={[{ required: true, message: 'Please select a unit type!' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select Unit Type"
                                        value={form.getFieldValue('product_unit')} // <-- make it controlled
                                        onChange={(value) => {

                                            // Optional: do something with the selected unit
                                            const selected = unittypes.find((ut) => ut.ut_id === value);
                                            console.log('Selected unit type:', selected);
                                        }}
                                        options={[
                                            ...(unittypes || []).map((ut) => ({
                                                label: ut.ut_name,
                                                value: ut.ut_id,
                                            })),
                                        ]}
                                        allowClear
                                        filterOption={(input, option) =>
                                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                        }
                                        style={{ flex: 1, minWidth: 0 }}
                                    />
                                </Form.Item>

                                <Button
                                    style={{ padding: 0 }}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsUnitTypeModalVisible(true)}
                                />
                            </AntInput.Group>
                        </Form.Item>

                        {/* Reusable GST Modal */}
                        <UnitTypeModel
                            visible={isUnitTypeModalVisible}
                            onCancel={() => setIsUnitTypeModalVisible(false)}
                            onCreate={handleUnitTypeCreated}
                            isEditMode={false}
                            initialValues={null}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_unit_value"
                            label="Product Unit Value"
                            rules={[
                                { required: true, message: 'Please enter the Product Qty' },
                                {
                                    pattern: /^\d+(\.\d{1,2})?$/,
                                    message: 'Enter a valid price (up to 2 decimals)',
                                },
                            ]}
                        >
                            <Input placeholder="e.g., 199.99" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_qty"
                            label="Product QTY"
                            rules={[
                                { required: true, message: 'Please enter the Product Qty' },
                                {
                                    pattern: /^\d+(\.\d{1,2})?$/,
                                    message: 'Enter a valid price (up to 2 decimals)',
                                },
                            ]}
                        >
                            <Input placeholder="e.g., 199.99" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_price"
                            label="Product Price"
                            rules={[
                                { required: true, message: 'Please enter the Product Price' },
                                {
                                    pattern: /^\d+(\.\d{1,2})?$/,
                                    message: 'Enter a valid price (up to 2 decimals)',
                                },
                            ]}
                        >
                            <Input placeholder="e.g., 199.99" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_mrp"
                            label="Product MRP"
                            rules={[
                                { required: true, message: 'Please enter the Product MRP' },
                                {
                                    pattern: /^\d+(\.\d{1,2})?$/,
                                    message: 'Enter a valid price (up to 2 decimals)',
                                },
                            ]}
                        >
                            <Input placeholder="e.g., 199.99" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="product_discount_price"
                            label="Product Discount Price"
                            rules={[
                                { required: true, message: 'Please enter the Product Discount Price' },
                                {
                                    pattern: /^\d+(\.\d{1,2})?$/,
                                    message: 'Enter a valid price (up to 2 decimals)',
                                },
                            ]}
                        >
                            <Input placeholder="e.g., 199.99" />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Product
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ProductModal;
